import { CSSProperties, MutableRefObject, ReactNode } from 'react';
// 常量
import { SimpleFormItemProps } from '../SimpleForm';
import { ColumnType } from 'antd/lib/table';
import { FormInstance, Rule } from 'antd/lib/form';
import { ITotalItem } from '../SuperTable/subpage/tableTotal';
import { SkuInfoListProps } from '../SkuInfoList';
import { TooltipProps } from 'antd/lib/tooltip';
import { ColumnsRouteLinkProps } from '../SuperTable/interface';
import { CopyTableDataProps } from '../CopyTableData';
/** 可编辑表 */
export interface EdiTableProps extends BaseTotalTableProps {
  /** 列配置 */
  columns?: EdiTableColumnsType[];
  /** 对外方法 */
  actionRef?: MutableRefObject<EditTableActionType>;
  /** 数据 */
  dataSource?: any[];
  /** 禁用可编辑表所有表单 */
  disabled?: boolean;
  /** 是否展示外边框和列边框 */
  bordered?: boolean;
  /** 是否需要排序字段 */
  isSort?: boolean;
  /** 高度 */
  height?: number;
  /** 排序字段名 */
  sortFieldName?: string;
  /** 排序单元格配置 */
  sortColumnType?: ColumnType<any> | boolean;
  /** 是否开启拖动排序(开启拖动排序后同时也会开启排序字段但不显示排序列) */
  isMove?: boolean;
  /** 拖动单元格配置（只有开启拖动排序才会生效） */
  moveColumnType?: ColumnType<any>;
  /** 操作列配置 */
  operation?: ColumnType<any> | false;
  /** 自定义操作按钮 */
  operationTitle?: ReactNode;
  /** 操作列宽度 */
  operationWidth?: number;
  /** 禁用行操作 */
  disabledRowOperation?: (record: any) => boolean;
  /** 是否隐藏操作列标题单元格内容 */
  hideAdd?: boolean;
  /** 操作列标题按钮图标 */
  addIcon?: ReactNode;
  /** 提交失败自动滚动到第一个错误字段 */
  scrollToFirstError?: boolean;
  /** 可编辑表单验证失败时是否出现提示框 */
  validateErrorTip?: boolean;
  /** 复制操作配置 */
  copyTableDataProps?: CopyTableDataProps;
  /** 添加数据之前回调 */
  onBeforeAdd?: (addDataSource?: any) => void;
  /** 删除数据之前回调 */
  onBeforeDelete?: (deleteDataSource?: any) => void;
  /** 数据校验错误回调，传了该方法则错误提示需自行实现 */
  onValidateError?: (errorLines?: number[]) => void;
  /**
   * 字段值更新时触发回调事件
   * @param changedField 当前修改的字段名称
   * @param changedValue 当前字段修改后的值
   * @param changedRowKey 当前行的key
   * @param changedRowValues 当前行的表单值
   * @returns 
   */
  onValuesChange?: (changedField: string, changedValue: any, changedRowKey: number, changedRowValues: any) => void;
};
/** 可编辑表行 */
export interface EditableRowProps {
  /** 样式 */
  style?: CSSProperties;
  /** 单元格数据集合 */
  children?: any[];
  /** 是否开启拖动排序 */
  move?: boolean;
  /** 第几行 */
  index?: number;
  /** 行标识 */
  'data-row-key'?: any;
}
/** 可编辑表列 */
export interface EditableCellProps{
  /** 可编辑表节点 */
  tableElement: HTMLElement | null;
  /** 当前行标识 */
  rowKey: number;
  /** form对象 */
  form?: FormInstance<any>;
  /** 单元格内容 */
  children?: ReactNode;
  /** form表单配置 */
  formItemProps?: SimpleFormItemProps;
  /** 是否可编辑 */
  editable?: boolean;
  /** 当前行数据 */
  record?: any;
  /** 第几行 */
  rowIndex: number;
}
/** 可编辑表单元格配置 */
export interface EdiTableColumnsType extends ColumnType<any>, Omit<SimpleFormItemProps, 'children'> {
  /** 是否可编辑 */
  editable?: boolean;
  /** 枚举名称 */
  enumCode?: string;
  /** 表单类型 */
  formItemType?: EditFormItemType;
  /** 是否必填 */
  required?: boolean;
  /** 当前行数据 */
  record?: any;
  /** 第几行 */
  rowIndex?: number;
  /** 禁用 */
  disabled?: boolean | ((record: any) => boolean);
  /** 禁用时展示字段 */
  disabledRenderKey?: string;
  /** 单元格类型 */
  renderType?: 'image' | 'images' | 'boolean' | 'skuInfoList' | 'copy' | 'routeLink';
  /** 子标题配置 */
  children?: EdiTableColumnsType[];
  /** SKU信息列 */
  skuInfoListProps?: SkuInfoListProps;
  /** 文字提示 */
  tooltip?: string | TooltipProps;
  /** 链接 */
  routeLinkProps?: ColumnsRouteLinkProps;
  /** 自定义验证条件 */
  formatRules?: (record: any) => Rule[];
}
/** 统计 */
export interface BaseTotalTableProps {
  /** 需要总计的字段 */
  totalKeys?: ITotalItem[];
  /** 第几列显示合计 */
  totalIndex?: number;
  /** 合计列展示文字 */
  totalText?: ReactNode;
}
/** 表单类型 */
export type EditFormItemType =
  | 'Input'
  | 'InputNumber'
  | 'SelectCustom'
  | 'SuperSelect'
  | 'SuperSelectTable'
  | 'Switch'
  | 'TextArea'
  | 'UploadImage'
  | 'UploadImages'
  | 'Upload'
  | 'Address'
  | 'CheckboxGroup'
  | 'Checkbox'
  | 'DatePicker'
  | 'RangePicker'
  | 'TimePicker'
  | 'Cascader'
  | 'DropdownTable'
  | 'DropdownSyncTable'
  | 'SuperAutoComplete';
export interface ActionType {
  /**
   * 新增
   * @param addDataSource 新增的数据，传数组则新增多行
   * @param isEmpty 新增时是否清空列表已有的数据
   * @returns 
   */
  add: (addDataSource?: any | any[], isEmpty?: boolean) => void;
  /**
   * 在原有数据基础上追加可编辑表数据
   * @param appendItem 追加数据
   * @returns 
   */
  append: (appendItem: any[]) => void;
  /**
   * 覆盖可编辑表数据
   * @param addItem 新数据
   * @returns 
   */
  cover: (addItem?: any[]) => void;
  /**
   * 获取数据
   * @param filterDeleteData 是否过滤已删除数据
   * @returns 
   */
  getDataSource: (filterDeleteData?: boolean) => any[];
  /** 提交 */
  submit: () => Promise<any[] | false>;
  /**
   * 删除
   * @param deleteDataSource 需要删除的行数据，传数组则删除多行
   * @returns 
   */
  delete: (deleteDataSource?: any) => void;
  /** 清空数据 */
  empty: () => void;
  /** 修改统计数据 */
  changeTotal: (dataSource?: any[]) => void;
  /**
   * 修改表单的值(单行单项)
   * @param rowKey 当前行的key
   * @param field 字段名
   * @param value 字段值
   * @returns 
   */
  setFieldValue: (rowKey: number, field: string, value: any) => void;
  /**
   * 修改表单的值(单行多项)
   * @param rowKey 当前行的key
   * @param values 当前行要修改的表单数据
   * @returns 
   */
  setFieldsValue: (rowKey: number, values: any) => void;
  /**
   * 批量修改所有行单项数据
   * @param field 字段名
   * @param value 字段值
   * @returns 
   */
  setRowsValue: (field: string, value: any) => void;
  /**
   * 批量修改所有行多项数据
   * @param values 要修改的表单数据
   * @returns 
   */
  setRowsValues: (values: any) => void;
  /**
   * 修改行数据
   * @param changeData 要修改的数据
   * @returns 
   */
  changeRowData: (changeData: any) => void;
  /** 可编辑表节点 */
  tableElement: HTMLElement | null;
  /** form表单对象 */
  form: FormInstance<any>;
}

export type EditTableActionType = ActionType | undefined;

export const GROUP_NAME = 'edit-table-group';

/** Dispatch类型 */
export type EdiTableDispatchType =
  | 'init'
  | 'addRow'
  | 'deleteRow'
  | 'changeDataSource'
  | 'moveData'
  | 'append'
  | 'changeRowData'
  | 'cover';

/** Payload数据 */
export interface IEdiTablePayload {
  /** 可编辑表末尾行指针 */
  flagIndex?: number;
  /** 可编辑表数据 */
  dataSource?: any[];
  /** 替换dataSource数据 */
  updateDataSource?: any[];
  /** 新增数据 */
  addItem?: any;
  /** 追加数据 */
  appendItem?: any;
  /** 拖动开始行 */
  startIndex?: number;
  /** 拖动结束行 */
  endIndex?: number;
  /** 是否清空可编辑表数据 */
  isEmpty?: boolean;
  /** 要删除的key */
  deleteKeys?: number[];
  /** 可编辑表表单对象 */
  form?: FormInstance<any>;
  /** 要更新的行数据 */
  rowData?: any;
}

export const SORT_DATA_INDEX = 'sort-data-index';
